import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../useApi.js";
import { api, euro, nf } from "../api.js";
import type { PartRow } from "../types.js";
import { Page, Pill } from "../components/Shell.js";
import { TableSkeleton } from "../components/Skeleton.js";
import { toast } from "../toast.js";

interface PartsResponse { leadDays: number; safetyDays: number; items: PartRow[] }
interface Suggestion {
  items: { partId: string; sku: string; name: string; qty: number; totalCents: number }[];
  totalCents: number; basis: string;
}

export default function Parts() {
  const nav = useNavigate();
  const [cart, setCart] = useState<Record<string, number>>({});
  const [group, setGroup] = useState("alle");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const { data, error, loading, reload } = useApi<PartsResponse>("/api/parts");

  const byId = useMemo(() => new Map((data?.items ?? []).map((p) => [p.id, p])), [data]);
  const groups = useMemo(
    () => ["alle", ...Array.from(new Set((data?.items ?? []).map((p) => p.group)))],
    [data],
  );

  const list = (data?.items ?? [])
    .filter((p) => group === "alle" || p.group === group)
    .filter((p) => !q || `${p.name}${p.sku}`.toLowerCase().includes(q.toLowerCase()));

  const cartEntries = Object.entries(cart).filter(([, qty]) => qty > 0);
  const cartTotal = cartEntries.reduce((s, [id, qty]) => s + qty * (byId.get(id)?.priceCents ?? 0), 0);

  function add(partId: string, qty: number) {
    if (qty <= 0) return;
    setCart((c) => ({ ...c, [partId]: (c[partId] ?? 0) + qty }));
  }
  function step(partId: string, dir: 1 | -1) {
    const pack = byId.get(partId)?.packSize ?? 1;
    setCart((c) => {
      const next = Math.max(0, (c[partId] ?? 0) + dir * pack);
      const copy = { ...c };
      if (next === 0) delete copy[partId]; else copy[partId] = next;
      return copy;
    });
  }

  async function suggest() {
    setBusy(true);
    try {
      const s = await api.get<Suggestion>("/api/parts/suggestion");
      if (s.items.length === 0) { toast("Alle Bestände liegen über der Sicherheitsreichweite.", "info"); return; }
      setCart((c) => {
        const copy = { ...c };
        for (const i of s.items) copy[i.partId] = (copy[i.partId] ?? 0) + i.qty;
        return copy;
      });
      toast(`${s.items.length} Positionen übernommen`, "success");
    } finally { setBusy(false); }
  }

  async function placeOrder() {
    setBusy(true);
    try {
      await api.post("/api/orders", {
        isAuto: true,
        items: cartEntries.map(([partId, qty]) => ({ partId, qty })),
      });
      setCart({});
      await reload();
      toast("Bestellung ausgelöst", "success");
      nav("/bestellungen");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Bestellung fehlgeschlagen", "error");
    } finally { setBusy(false); }
  }

  return (
    <Page title="Ersatzteile"
          sub={data ? `${data.items.length} Artikel · Wiederbeschaffungszeit ${data.leadDays} Tage · Sicherheitsreichweite ${data.safetyDays} Tage` : undefined}
          actions={<>
            <input className="srch" placeholder="Artikel oder Positionsnummer…"
                   value={q} onChange={(e) => setQ(e.target.value)} />
            <button className="btn pri" disabled={busy} onClick={() => void suggest()}>
              Vorschlag erzeugen
            </button>
          </>}>
      {loading && <div className="card"><TableSkeleton /></div>}
      {error && <div className="empty">{error}</div>}
      {data && (
        <div className="grid g-parts">
          <div className="grid" style={{ gap: 14 }}>
            <div className="seg">
              {groups.map((g) => (
                <button key={g} aria-pressed={group === g} onClick={() => setGroup(g)}>
                  {g === "alle" ? "Alle Gruppen" : g}
                </button>
              ))}
            </div>

            <div className="card"><div className="tbl-wrap"><table>
              <thead><tr>
                <th>Artikel</th><th className="r">Bestand</th><th className="r">Mindest</th>
                <th className="r">Verbrauch</th><th className="r">Reichweite</th>
                <th>Status</th><th className="r">Vorschlag</th><th />
              </tr></thead>
              <tbody>
                {list.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {p.imagePath
                          ? <img src={p.imagePath} alt="" style={{
                              width: 32, height: 32, borderRadius: 6, objectFit: "cover",
                              border: "1px solid var(--line)", flex: "none",
                            }} />
                          : <div style={{
                              width: 32, height: 32, borderRadius: 6, background: "var(--surface-2)",
                              border: "1px dashed var(--line-2)", flex: "none",
                            }} />}
                        <div style={{ minWidth: 0 }}>
                          <b>{p.name}</b>
                          <div className="sub-t mono">Pos. {p.sku} · {p.group} · VE {p.packSize} St. · {euro(p.priceCents)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="r num">{nf(p.stock)}
                      {p.onOrder > 0 && <div className="sub-t num">+{nf(p.onOrder)} bestellt</div>}</td>
                    <td className="r num">{nf(p.minStock)}</td>
                    <td className="r num">{nf(p.monthlyUse, 1)}<div className="sub-t">St./Monat</div></td>
                    <td className="r num">{Number.isFinite(p.reachDays) ? `${p.reachDays} T` : "—"}</td>
                    <td><Pill status={p.status}>
                      {p.status === "CRITICAL" ? "Unter Mindestbestand"
                        : p.status === "WARNING" ? "Unter Zielbestand" : "Ausreichend"}
                    </Pill></td>
                    <td className="r num">{p.suggested > 0 ? <b>{nf(p.suggested)}</b> : "—"}</td>
                    <td className="r">
                      <button className="btn sm" onClick={() => add(p.id, p.suggested || p.packSize)}>
                        + {nf(p.suggested || p.packSize)}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div></div>

            <div className="card">
              <div className="card-h"><h3>So wird der Bedarf gerechnet</h3></div>
              <div className="card-b" style={{ fontSize: 13, color: "var(--ink-2)" }}>
                <p style={{ margin: "0 0 10px" }}>
                  Der Verbrauch stammt nicht aus einer Schätzung, sondern aus den Verschleißzählern aller
                  Geräte: Einblasmeter und Betriebsstunden geteilt durch das Wechselintervall des Teils.
                </p>
                <p className="mono" style={{
                  background: "var(--surface-2)", border: "1px solid var(--line)",
                  borderRadius: "var(--r-sm)", padding: "11px 13px", fontSize: 12,
                  color: "var(--ink)", margin: "0 0 10px",
                }}>
                  Zielbestand = Mindestbestand + Tagesbedarf × ({data.leadDays} Tage Lieferzeit + {data.safetyDays} Tage Puffer)<br />
                  Vorschlag&nbsp;&nbsp;&nbsp;&nbsp;= aufgerundet auf VE ( Zielbestand − Bestand − bereits bestellt )
                </p>
                <p style={{ margin: 0 }}>
                  Vorgeschlagen wird nur, was einen Zähler hat. Der Vorschlag wird nie automatisch bestellt –
                  die Freigabe bleibt beim Menschen.
                </p>
              </div>
            </div>
          </div>

          <div className="card sticky" style={{ position: "sticky", top: 86 }}>
            <div className="card-h">
              <h3>Bestellvorschlag</h3>
              <div className="spacer" />
              {cartEntries.length > 0 && (
                <button className="btn ghost sm" onClick={() => setCart({})}>Leeren</button>
              )}
            </div>
            <div className="card-b">
              {cartEntries.length === 0 ? (
                <div className="empty" style={{ padding: "22px 0" }}>
                  Noch nichts vorgemerkt.<br />
                  <span style={{ fontSize: 12 }}>„Vorschlag erzeugen" füllt die Liste aus den Verschleißzählern.</span>
                </div>
              ) : (
                <>
                  {cartEntries.map(([id, qty]) => {
                    const p = byId.get(id);
                    if (!p) return null;
                    return (
                      <div className="cart-row" key={id}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                          {p.imagePath
                            ? <img src={p.imagePath} alt="" style={{
                                width: 26, height: 26, borderRadius: 5, objectFit: "cover",
                                border: "1px solid var(--line)", flex: "none",
                              }} />
                            : <div style={{
                                width: 26, height: 26, borderRadius: 5, background: "var(--surface-2)",
                                border: "1px dashed var(--line-2)", flex: "none",
                              }} />}
                          <div style={{ minWidth: 0 }}>
                            <b style={{ fontSize: 12.5, display: "block", lineHeight: 1.3 }}>{p.name}</b>
                            <span className="sub-t mono">{p.sku} · {euro(p.priceCents)}</span>
                          </div>
                        </div>
                        <span className="qty">
                          <button onClick={() => step(id, -1)} aria-label="Menge verringern">−</button>
                          <span>{qty}</span>
                          <button onClick={() => step(id, 1)} aria-label="Menge erhöhen">+</button>
                        </span>
                        <span className="num" style={{ fontSize: 12.5, width: 66, textAlign: "right" }}>
                          {euro(qty * p.priceCents)}
                        </span>
                      </div>
                    );
                  })}
                  <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "baseline",
                    marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--line)",
                  }}>
                    <span className="sub-t">Summe netto</span>
                    <b className="num" style={{ fontFamily: "var(--f-display)", fontSize: 19 }}>{euro(cartTotal)}</b>
                  </div>
                  <button className="btn pri" disabled={busy}
                          style={{ width: "100%", justifyContent: "center", marginTop: 12 }}
                          onClick={() => void placeOrder()}>
                    Bestellung auslösen
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
